import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastVariant = 'default' | 'success' | 'error';
interface ToastItem { id: number; title: string; description?: string; variant?: ToastVariant }

const ToastCtx = React.createContext<{ push: (t: Omit<ToastItem, 'id'>) => void }>({ push: () => {} });

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const push = React.useCallback((t: Omit<ToastItem, 'id'>) => {
    setToasts((cur) => [...cur, { ...t, id: Date.now() + Math.random() }]);
  }, []);
  const dismiss = (id: number) => setToasts((cur) => cur.filter((t) => t.id !== id));
  return (
    <ToastCtx.Provider value={{ push }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            duration={4000}
            onOpenChange={(open) => { if (!open) dismiss(t.id); }}
            className={cn(
              'card p-4 flex items-start gap-3 min-w-[280px] data-[state=open]:animate-in data-[state=closed]:animate-out',
              t.variant === 'success' && 'border-success/30',
              t.variant === 'error' && 'border-danger/30'
            )}
          >
            <div className="flex-1">
              <ToastPrimitive.Title className="text-sm font-semibold">{t.title}</ToastPrimitive.Title>
              {t.description && <ToastPrimitive.Description className="text-xs text-neutral-500 mt-0.5">{t.description}</ToastPrimitive.Description>}
            </div>
            <ToastPrimitive.Close className="text-neutral-400 hover:text-neutral-700"><X className="h-4 w-4" /></ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed top-4 right-4 z-[100] flex flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return React.useContext(ToastCtx);
}
